// ============================================================
// sidebar.js — Comportamiento del sidebar + modal de ayuda
// ============================================================
import { qs, qsa, on } from './utils.js';
import { toast } from './toast.js';
import { translations } from './translations.js';
import { getUser } from './api.js';

const COLLAPSED_KEY = 'vimo:sidebar-collapsed';

export function initSidebar() {
  const sidebar = qs('#appSidebar');
  const backdrop = qs('#sidebarBackdrop');
  if (!sidebar) return;

  const openBtn = qs('[data-action="open-sidebar"]');
  const toggleBtn = qs('[data-action="toggle-sidebar"]');

  // Restaurar estado colapsado guardado (solo aplica en escritorio)
  if (localStorage.getItem(COLLAPSED_KEY) === '1') {
    sidebar.classList.add('is-collapsed');
  }

  const openSidebar = () => {
    sidebar.classList.add('is-open');
    backdrop?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
  };

  const closeSidebar = () => {
    sidebar.classList.remove('is-open');
    backdrop?.classList.remove('is-visible');
    document.body.style.overflow = '';
  };

  openBtn?.addEventListener('click', openSidebar);
  backdrop?.addEventListener('click', closeSidebar);
  toggleBtn?.addEventListener('click', () => {
    // En móvil/tablet: cierra el panel desplegable.
    // En escritorio: colapsa/expande y guarda el estado.
    if (window.matchMedia('(max-width: 992px)').matches) {
      closeSidebar();
    } else {
      sidebar.classList.toggle('is-collapsed');
      localStorage.setItem(
        COLLAPSED_KEY,
        sidebar.classList.contains('is-collapsed') ? '1' : '0'
      );
    }
  });

  // Cierra al pulsar Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSidebar();
  });

  // Marca el enlace activo según la página
  const page = document.body.dataset.page;
  if (page) {
    qsa(`.sidebar-link[data-nav="${page}"]`).forEach((a) => a.classList.add('active'));
  }

  // Cierra el sidebar móvil cuando se hace clic en un enlace
  on(sidebar, 'click', '.sidebar-link', () => {
    if (window.matchMedia('(max-width: 992px)').matches) closeSidebar();
  });

  // ── Modal de ayuda ──────────────────────────────────────────
  const helpModal = qs('#helpModal');
  if (!helpModal) return;

  const openHelp  = () => { 
    helpModal.hidden = false; 
    const user = getUser();
    if (user) {
      const nombreInput = qs('#helpNombre');
      const emailInput = qs('#helpEmail');
      if (nombreInput && !nombreInput.value) nombreInput.value = user.name || '';
      if (emailInput && !emailInput.value) emailInput.value = user.email || '';
    }
    const msgInput = qs('#helpMsg');
    if (msgInput) msgInput.focus();
    else helpModal.querySelector('input')?.focus(); 
  };
  const closeHelp = () => { helpModal.hidden = true; };

  on(document, 'click', '[data-action="open-help"]',  openHelp);
  on(document, 'click', '[data-action="close-help"]', closeHelp);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !helpModal.hidden) closeHelp();
  });

  qs('[data-help-form]')?.addEventListener('submit', (e) => {
    e.preventDefault();
    const fd      = new FormData(e.target);
    const payload = Object.fromEntries(fd.entries());
    console.log('[Vimo] help form', payload);
    // TODO backend: POST /api/support { nombre, email, mensaje }
    closeHelp();
    e.target.reset();
    toast({ message: '¡Mensaje enviado! Te responderemos en breve.', type: 'success' });
  });

  // Redirige a precios cuando el usuario pulsa "Actualizar a Premium" o el banner
  on(document, 'click', '[data-action="upgrade"]', () => {
    window.location.href = 'precios.html';
  });

  // ── Selector de Idioma ──────────────────────────────────────
  const langSelector = qs('.lang-selector');
  if (langSelector) {
    const langBtn = qs('#langDropdownBtn');
    const langCurrent = qs('.lang-current');
    const options = qsa('.lang-option');
    
    // Recuperar idioma guardado
    const savedLang = localStorage.getItem('vimo:lang') || 'es';
    
    // Función para establecer idioma visible y traducir elementos
    const setLanguage = (lang) => {
      localStorage.setItem('vimo:lang', lang);
      
      // Actualizar bandera en el botón principal con imágenes (FlagCDN) para soporte multiplataforma
      const flagUrls = { 
        es: 'https://flagcdn.com/w20/es.png', 
        en: 'https://flagcdn.com/w20/gb.png', 
        pt: 'https://flagcdn.com/w20/br.png' 
      };
      if (langCurrent) {
        langCurrent.innerHTML = `<img src="${flagUrls[lang] || flagUrls.es}" width="20" alt="${lang.toUpperCase()}" style="border-radius:2px; vertical-align: middle;">`;
      }
      
      // Actualizar clase activa en las opciones
      options.forEach(opt => {
        if (opt.dataset.lang === lang) {
          opt.classList.add('is-active');
        } else {
          opt.classList.remove('is-active');
        }
      });

      // Aplicar traducciones
      const dict = translations[lang] || translations.es;
      
      qsa('[data-i18n]').forEach(el => {
        const key = el.getAttribute('data-i18n');
        if (dict[key]) {
          if (dict[key].includes('<')) {
            el.innerHTML = dict[key];
          } else {
            el.textContent = dict[key];
          }
        }
      });

      qsa('[data-i18n-placeholder]').forEach(el => {
        const key = el.getAttribute('data-i18n-placeholder');
        if (dict[key]) {
          el.setAttribute('placeholder', dict[key]);
        }
      });

      qsa('[data-i18n-aria-label]').forEach(el => {
        const key = el.getAttribute('data-i18n-aria-label');
        if (dict[key]) {
          el.setAttribute('aria-label', dict[key]);
        }
      });
      
      console.log(`[Vimo] Idioma cambiado a: ${lang}`);
    };
    
    // Inicializar idioma al cargar
    setLanguage(savedLang);

    // Abrir/cerrar dropdown
    langBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      langSelector.classList.toggle('is-open');
    });

    // Cerrar al hacer clic fuera
    document.addEventListener('click', (e) => {
      if (!langSelector.contains(e.target)) {
        langSelector.classList.remove('is-open');
      }
    });

    // Cambiar idioma al hacer clic en una opción
    options.forEach(opt => {
      opt.addEventListener('click', () => {
        const lang = opt.dataset.lang;
        setLanguage(lang);
        langSelector.classList.remove('is-open');
        
        // Mensaje de toast internacionalizado
        const langNames = { es: 'Español', en: 'English', pt: 'Português' };
        const toastMsgs = {
          es: `Idioma cambiado a ${langNames[lang]}`,
          en: `Language changed to ${langNames[lang]}`,
          pt: `Idioma alterado para ${langNames[lang]}`
        };
        toast({ message: toastMsgs[lang] || toastMsgs.es, type: 'success' });
      });
    });
  }
}
